import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, Star, Navigation, Quote } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Container, PageHeader, SectionHead, WhatsappIcon, Honeypot } from "../components/ui-kit";
import { CONTACT, BRANCHES } from "../mock";
import { postContact } from "../api";
import { useReviews } from "../hooks/useReviews";
import { toast } from "sonner";

const inputCls =
  "w-full h-12 px-4 bg-canvas border border-line text-sm text-ink focus:outline-none focus:border-ink focus:bg-white placeholder:text-mute transition-colors";
const labelCls = "block text-[11px] font-semibold tracking-[0.16em] uppercase text-mute mb-2";

const RatingBadge = ({ rating, count, className = "" }) => (
  <div className={`inline-flex items-center gap-4 border border-line px-6 py-4 bg-canvas ${className}`} data-testid="contact-rating-badge">
    <span className="font-display font-light text-4xl text-ink leading-none">{rating}</span>
    <div>
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={13} className="fill-brand text-brand" />
        ))}
      </div>
      <p className="text-[12px] text-mute mt-1">Google'da {count} değerlendirme</p>
    </div>
  </div>
);

const Contact = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "", message: "", website: "", consent: false });
  const [sending, setSending] = useState(false);
  const rev = useReviews();
  const navigate = useNavigate();

  useEffect(() => {
    const urun = params.get("urun");
    if (urun) {
      setForm((f) => ({
        ...f,
        subject: "Ürün Teklifi",
        message: `Merhaba, "${urun}" ürünü için fiyat ve numune talebinde bulunmak istiyorum.`,
      }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Lütfen ad, e-posta ve mesaj alanlarını doldurun.");
      return;
    }
    if (!form.consent) {
      toast.error("Devam etmek için Aydınlatma Metni onayını işaretleyin.");
      return;
    }
    setSending(true);
    try {
      await postContact(form);
      setForm({ name: "", company: "", email: "", phone: "", subject: "", message: "", website: "", consent: false });
      navigate("/tesekkurler");
    } catch (err) {
      toast.error(err.message || "Mesaj gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, t: "Telefon", c: <a href={CONTACT.phoneHref} className="hover:text-brand transition-colors" data-testid="contact-phone-link">{CONTACT.phone}</a> },
    {
      icon: WhatsappIcon,
      t: "WhatsApp Sipariş Hattı",
      c: (
        <span className="flex flex-col gap-1">
          <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors" data-testid="contact-whatsapp-link">
            {CONTACT.whatsapp}
          </a>
          <a href={CONTACT.whatsappHref2} target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors" data-testid="contact-whatsapp-link-2">
            {CONTACT.whatsapp2}
          </a>
        </span>
      ),
    },
    {
      icon: Mail,
      t: "E-Posta",
      c: (
        <span className="flex flex-col gap-1">
          {CONTACT.emails.map((m) => (
            <a key={m} href={`mailto:${m}`} className="hover:text-brand transition-colors">{m}</a>
          ))}
        </span>
      ),
    },
    { icon: MapPin, t: "Adres", c: <>{CONTACT.address}<br /><span className="text-mute text-[12px]">{CONTACT.branch}</span></> },
    { icon: Clock, t: "Çalışma Saatleri", c: <>Hafta içi 08:30 – 18:30<br />Cumartesi 08:30 – 14:00</> },
  ];

  return (
    <main className="bg-canvas min-h-screen" data-testid="contact-page">
      <Seo
        title="İletişim | Teklif & Numune Talebi — Koşan Düğme"
        description="Koşan Düğme ile iletişime geçin. İletişim formu, WhatsApp sipariş hattı, telefon ve Güngören fabrika ile Merter şube adresleri."
        path="/iletisim"
      />
      <Breadcrumbs items={[{ label: "İletişim", to: "/iletisim" }]} />
      <PageHeader
        eyebrow="Bize Ulaşın"
        title="İletişim"
        text="Formu doldurun, WhatsApp'tan yazın ya da doğrudan arayın; talebinizi aynı gün değerlendirip size dönüş yapıyoruz."
      >
        <div className="hidden lg:flex justify-end">
          <RatingBadge rating={rev.rating} count={rev.review_count} className="bg-paper" />
        </div>
      </PageHeader>

      {/* WhatsApp şeridi */}
      <div className="border-b border-line bg-[#128C7E]/[0.06]" data-testid="contact-whatsapp-strip">
        <Container className="py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="flex items-center gap-3 text-[14px] text-ink">
            <span className="w-9 h-9 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0">
              <WhatsappIcon size={17} />
            </span>
            Siparişleriniz için hızlı WhatsApp hattımızdan bize ulaşın.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="group inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white text-[13px] font-semibold transition-colors"
          >
            <WhatsappIcon size={16} />
            WhatsApp'tan Yaz · {CONTACT.whatsapp}
          </a>
        </Container>
      </div>

      <Container className="py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form onSubmit={submit} noValidate className="bg-paper border border-line p-7 sm:p-10" data-testid="contact-form">
            <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand">İletişim Formu</p>
            <h2 className="font-display font-light text-2xl sm:text-3xl text-ink mt-3 mb-9">Talebinizi iletin</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Ad Soyad *</label>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Adınız Soyadınız" data-testid="contact-name-input" />
              </div>
              <div>
                <label className={labelCls}>Firma</label>
                <input className={inputCls} value={form.company} onChange={set("company")} placeholder="Firma adı (opsiyonel)" data-testid="contact-company-input" />
              </div>
              <div>
                <label className={labelCls}>E-Posta *</label>
                <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="ornek@firma.com" data-testid="contact-email-input" />
              </div>
              <div>
                <label className={labelCls}>Telefon</label>
                <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+90 ___ ___ __ __" data-testid="contact-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Konu</label>
                <input className={inputCls} value={form.subject} onChange={set("subject")} placeholder="Ürün teklifi, numune talebi…" data-testid="contact-subject-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Mesajınız *</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Talebinizi kısaca yazınız…"
                  data-testid="contact-message-input"
                  className="w-full px-4 py-3 bg-canvas border border-line text-sm text-ink focus:outline-none focus:border-ink focus:bg-white placeholder:text-mute transition-colors resize-y"
                />
              </div>
            </div>
            <Honeypot value={form.website} onChange={set("website")} />
            <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="contact-consent">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                data-testid="contact-consent-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#D9261C] shrink-0"
              />
              <span className="text-[12.5px] text-ink2 leading-relaxed">
                <Link to="/kvkk-aydinlatma-metni" target="_blank" className="text-ink font-medium hover:text-brand transition-colors underline">KVKK Aydınlatma Metni</Link>
                'ni okudum; kişisel verilerimin talebimin karşılanması amacıyla işlenmesini kabul ediyorum.
              </span>
            </label>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-6 inline-flex items-center gap-2.5 h-12 px-8 rounded-full bg-brand hover:bg-brandDark disabled:opacity-60 text-white text-[13px] font-semibold transition-all"
            >
              {sending ? "Gönderiliyor…" : "Gönder"}
              <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
        </Reveal>

        {/* Bilgi */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="bg-paper border border-line divide-y divide-line" data-testid="contact-info">
              {info.map((b) => (
                <div key={b.t} className="p-6 flex gap-5">
                  <div className="w-10 h-10 shrink-0 rounded-full border border-line flex items-center justify-center text-brand">
                    <b.icon size={17} strokeWidth={1.6} />
                  </div>
                  <div>
                    <h3 className="text-[11px] font-semibold tracking-[0.2em] uppercase text-mute">{b.t}</h3>
                    <p className="text-[14px] font-medium text-ink mt-1.5 leading-relaxed">{b.c}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Container>

      {/* Şubeler */}
      <section className="pb-20 lg:pb-28" data-testid="contact-branches">
        <Container>
          <Reveal>
            <SectionHead eyebrow="Şubelerimiz" title="Fabrika ve Merter şubemiz" />
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-6 mt-12">
            {BRANCHES.map((b, i) => (
              <Reveal key={b.id} delay={i * 120} className="h-full">
                <div className="bg-paper border border-line overflow-hidden h-full flex flex-col" data-testid={`branch-card-${b.id}`}>
                  {b.media ? (
                    <div data-testid="merter-media">
                      <div className="grid grid-cols-2">
                        {b.media.photos.map((ph, idx) => (
                          <div
                            key={idx}
                            className={`aspect-[4/3] overflow-hidden bg-plate group/ph ${idx === 0 ? "border-r border-line" : ""}`}
                          >
                            <img
                              src={ph.image}
                              alt={ph.label}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover/ph:scale-105"
                              data-testid={`merter-photo-${idx}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <iframe
                      title={b.name}
                      src={b.mapsEmbed}
                      className="w-full h-[280px] grayscale hover:grayscale-0 transition-all duration-700"
                      loading="lazy"
                    />
                  )}
                  <div className="p-7 flex-1 flex flex-col">
                    <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-brand">{b.name}</p>
                    <p className="flex gap-3 text-[14px] text-ink2 leading-relaxed mt-4">
                      <MapPin size={16} className="text-ink shrink-0 mt-0.5" strokeWidth={1.6} />
                      {b.address}
                    </p>
                    <p className="flex gap-3 text-[14px] text-ink font-medium mt-3">
                      <Phone size={16} className="text-ink shrink-0 mt-0.5" strokeWidth={1.6} />
                      <a href={b.phoneHref} className="hover:text-brand transition-colors">{b.phone}</a>
                    </p>
                    <a
                      href={b.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`branch-directions-${b.id}`}
                      className="group mt-auto pt-6 inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.12em] uppercase text-ink hover:text-brand transition-colors"
                    >
                      <Navigation size={13} className="group-hover:translate-x-0.5 transition-transform" />
                      Yol tarifi al
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Yorumlar */}
      <section className="py-20 lg:py-28 bg-paper border-t border-line" data-testid="contact-reviews">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Müşteri Yorumları"
              title="Müşterilerimiz ne diyor?"
              action={<RatingBadge rating={rev.rating} count={rev.review_count} />}
            />
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {rev.reviews.map((r, i) => (
              <Reveal key={`${r.author}-${i}`} delay={(i % 3) * 90} className="h-full">
                <div className="h-full bg-canvas border border-line p-6 flex flex-col hover:border-ink/30 transition-colors" data-testid={`review-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-0.5">
                      {[...Array(r.rating || 5)].map((_, s) => (
                        <Star key={s} size={12} className="fill-brand text-brand" />
                      ))}
                    </div>
                    <Quote size={18} className="text-line" />
                  </div>
                  <p className="text-ink2 text-[14px] leading-relaxed mt-4 flex-1">{r.text}</p>
                  <div className="flex items-center gap-3 mt-6 pt-5 border-t border-line">
                    {r.profile_photo ? (
                      <img src={r.profile_photo} alt={r.author} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-ink text-white text-[13px] font-semibold flex items-center justify-center">{r.initial}</span>
                    )}
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-ink truncate">{r.author}</p>
                      <p className="text-[11px] text-mute">{r.relative_time}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {rev.source === "google" && (
            <p className="text-center text-[12px] text-mute mt-8" data-testid="reviews-attribution">
              Değerlendirmeler Google tarafından sağlanmaktadır · alaka düzeyine göre en fazla 5 yorum gösterilir.
            </p>
          )}

          <Reveal delay={200}>
            <div className="mt-12 text-center">
              <a
                href={rev.maps_url || BRANCHES[1].mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-google-link"
                className="group inline-flex items-center gap-2.5 h-12 px-7 rounded-full border border-ink/20 text-ink text-[13px] font-semibold hover:bg-ink hover:text-white hover:border-ink transition-all"
              >
                Tüm yorumları Google'da gör
                <Navigation size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>
        </Container>
      </section>
    </main>
  );
};

export default Contact;
